<script setup lang="ts">
/**
 * ノイズゲームのメイン画面
 */
import { onMounted, onUnmounted, watch, computed } from 'vue';
import { useRoomStore } from '@/stores/room';
import { useGameStore } from '@/stores/game';
import ImageDisplay from '@/components/ImageDisplay.vue';
import AnswerChoices from '@/components/AnswerChoices.vue';
import FinalScoreboard from '@/components/FinalScoreboard.vue';
import DebugGameState from '@/components/DebugGameState.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const gameStore = useGameStore();
const roomStore = useRoomStore();
const router = useRouter();
const { t } = useI18n();
// シングルプレイ用の固定ルームIDを設定
const roomId = 'single_player_room';

// 開発モード判定
const isDev = import.meta.env.DEV;


// ゲームがマウントされたら、WebSocket接続を確立し、問題を取得する
onMounted(async () => {
  console.log('NoiseGameView.vue: onMounted - Establishing WebSocket connection...');
  // 2. WebSocket接続 (roomStoreを使用)
  // 2. WebSocket接続 (roomStoreを使用)
  // roomStore.connectRoomWebSocket(roomId, gameStore); -> await in try block

  console.log('NoiseGameView.vue: onMounted - Fetching initial question data via WebSocket...');
  try {
    await roomStore.connectRoomWebSocket(roomId, gameStore);
    console.log('NoiseGameView: WebSocket connected successfully.');

    // 3. ゲーム開始
    await gameStore.startGameViaWebSocket(roomId, 'noise'); // WebSocket経由でゲーム開始と問題取得
    if (gameStore.questions.length > 0) {
      console.log('NoiseGameView.vue: Game started and initial question data received via WebSocket.');
    }
  } catch (error) {
    console.error('NoiseGameView.vue: Failed to start game via WebSocket:', error);
  }
});

// コンポーネントがアンマウントされたらWebSocket接続を閉じる
onUnmounted(() => {
  console.log('NoiseGameView.vue: onUnmounted - Disconnecting WebSocket.');
  roomStore.disconnectRoomWebSocket();
  gameStore.stopTimer();
});

// currentQuestionが更新されたらタイマーをリセットして再開
watch(() => gameStore.currentQuestion, (newQuestion) => {
  console.log('NoiseGameView.vue: gameStore.currentQuestionが更新されたよ:', newQuestion);
  if (newQuestion && newQuestion.game_details) {
    console.log('NoiseGameView.vue: ゲームタイプ:', newQuestion.game_details.game_type);
    console.log('NoiseGameView.vue: ノイズ設定:', newQuestion.game_details.settings);
  }
  // 問題が更新されたらストアのタイマーをリセットして再開 -> ストア側で制御するため削除
  // if (newQuestion) {
  //   gameStore.stopTimer();
  //   gameStore.startTimer();
  // }
}, { immediate: true });

watch(() => gameStore.currentImageUrl, (newImage) => {
  console.log('NoiseGameView.vue: gameStore.currentImageUrlが更新されたよ:', newImage ? '画像データあり' : '画像データなし');
});

const handleChoiceSelected = (choiceId: number) => {
  gameStore.selectAnswer(choiceId);
  gameStore.stopTimer();
};



const currentProgress = computed(() => {
  let count = gameStore.currentDisplayQuestionNumber - 1;
  if (gameStore.isAnswered) {
    count++;
  }
  return Math.max(0, count);
});

const handleRetry = async () => {
  await gameStore.startGameViaWebSocket(roomId, 'noise');
};

const handlePlayAnother = () => {
  router.push({ name: 'SinglePlaySelection' });
};

const handleBack = () => {
  router.push({ name: 'Home' });
};
</script>

<template>
  <div class="game-container">
    <!-- デバッグ用オーバーレイ。開発中のみ表示されます -->
    <DebugGameState v-if="isDev" />
    <h1 v-if="!gameStore.currentQuestion && !gameStore.isGameFinished">{{ t('game.loading_question') }}</h1>

    <!-- ゲーム終了メッセージ -->
    <div v-if="gameStore.isGameFinished" class="game-end-message">
      <h2>{{ t('game.game_over') }}</h2>
      <!-- 最終スコアボードの表示 -->
      <FinalScoreboard :final-scores="gameStore.finalScores" :total-questions="gameStore.totalQuestions"
        @retry="handleRetry" @play-another="handlePlayAnother" @back="handleBack" />
    </div>

    <!-- 問題表示エリア -->
    <template v-else-if="gameStore.currentQuestion && gameStore.currentQuestion.game_details">
      <div class="header-info">
        <div class="time-display info-badge">
          <span class="label">{{ t('game.time_remaining') }}</span>
          <!-- カウントダウン中は前の問題の残り時間が見えないように --- を表示 -->
          <span class="value">{{ gameStore.isCountdownActive ? '---' : gameStore.remainingTime.toFixed(3) + 's'
            }}</span>
        </div>
        <div class="answered-count info-badge">
          <span class="label">{{ t('game.answer_count') }}</span>
          <span class="value">{{ currentProgress }} / {{ gameStore.totalQuestions }}</span>
        </div>
      </div>
      <ImageDisplay :key="gameStore.currentQuestion.id" :image-url="gameStore.currentImageUrl" />

      <div class="game-controls">
        <!-- 回答結果の表示 -->
        <transition name="fade">
          <div v-if="gameStore.answerResult" class="answer-result"
            :class="gameStore.answerResult.isCorrect ? 'correct' : 'incorrect'">
            {{ gameStore.answerResult.message }}
          </div>
        </transition>

        <!-- 選択肢の表示 (まだ回答してない場合) -->
        <transition name="fade">
          <AnswerChoices v-if="!gameStore.isAnswered" :choices="gameStore.currentQuestion.choices"
            @select-choice="handleChoiceSelected" />
        </transition>
      </div>
    </template>
  </div>
</template>

<style scoped>
.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: auto;
  /* 100vh -> auto */
  padding: 0.2rem;
  box-sizing: border-box;
  font-family: 'Arial', sans-serif;
  text-align: center;
}

h1 {
  margin-bottom: 2rem;
  color: #333;
}

h2 {
  font-size: 2.5rem;
  color: #007bff;
  margin-bottom: 1rem;
}

.question-counter {
  margin-top: 1rem;
  font-size: 1.2rem;
  color: #666;
}

.answer-result {
  margin-top: 0;
  margin-bottom: 0;
  font-size: 2rem;
  font-weight: bold;
  padding: 1rem 2rem;
  border-radius: 10px;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.game-controls {
  width: 100%;
  max-width: 320px;
  /* 350px -> 320px */
  min-height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  /* 3px -> 5px */
  margin-top: 5px;
  /* 3px -> 5px */
  padding-bottom: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.correct {
  color: #fff;
  background-color: #28a745;
  border: 3px solid #1e7e34;
}

.incorrect {
  color: #fff;
  background-color: #dc3545;
  border: 3px solid #b21f2d;
}

.game-end-message {
  margin-top: 5px;
  /* 3rem -> 5px */
  font-size: 2.5rem;
  font-weight: bold;
  color: #007bff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.header-info {
  display: flex;
  justify-content: center;
  gap: 0.2rem;
  width: 100%;
  max-width: 320px;
  /* 350px -> 320px */
  margin-bottom: 5px;
  /* 3px -> 5px */
  flex-wrap: nowrap;
}

.info-badge {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.8rem 1.2rem;
  min-width: 0;
  flex: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
  white-space: nowrap;
}

.info-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.08);
}

.info-badge .label {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-badge .value {
  font-size: 1.4rem;
  font-weight: 800;
  color: #495057;
  font-family: 'Roboto Mono', monospace;
}

@media (max-width: 600px) {
  .header-info {
    gap: 0.5rem;
  }

  .info-badge {
    padding: 0.5rem 0.5rem;
    flex-direction: column;
    align-items: center;
  }

  .info-badge .value {
    font-size: 1.1rem;
  }

  .info-badge .label {
    font-size: 0.7rem;
    margin-bottom: 0;
  }
}

/* Tablet Layout */
@media (min-width: 601px) and (max-width: 1023px) {

  .header-info,
  .game-controls {
    max-width: 480px;
  }
}

/* PC Layout */
@media (min-width: 1024px) {
  .game-container {
    display: grid;
    grid-template-columns: 1fr 350px;
    grid-template-rows: auto 1fr;
    grid-template-areas:
      "image info"
      "image controls";
    gap: 2rem;
    min-height: 100vh;
    align-content: center;
    justify-content: center;
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
  }

  h1,
  .game-end-message {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
    padding: 2rem;
    align-self: center;
    justify-self: center;
  }

  .header-info {
    grid-area: info;
    margin-bottom: 0;
    align-self: end;
    max-width: 100%;
    width: 100%;
  }

  .game-controls {
    grid-area: controls;
    margin-top: 0;
    align-self: start;
    max-width: 100%;
    width: 100%;
  }

  :deep(.image-display-container) {
    grid-area: image;
    margin-bottom: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.time-display .value {
  color: #333;
}
</style>
